import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Form, Button, Spinner, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId, getPositionId } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';

const API = process.env.REACT_APP_API;

const FIELDS = [
  { key: 'st_everSyndrome',   label: 'โรคที่เคยเป็น' },
  { key: 'st_drug',           label: 'การแพ้ยา' },
  { key: 'st_food',           label: 'การแพ้อาหาร' },
  { key: 'st_syndrome',       label: 'โรคประจำตัว' },
  { key: 'st_facingSyndrome', label: 'วิธีแก้ไขเมื่อเจ็บป่วยด้วยโรคประจำตัว' },
  { key: 'st_accidentHistory',label: 'อุบัติเหตุร้ายแรงและความผิดปกติอื่นๆ' },
];

const EMPTY = Object.fromEntries(FIELDS.map(f => [f.key, '']));

// ผลลัพธ์จาก tb_nurse_visit.nv_outcome — ชุดเดียวกับที่ห้องพยาบาลใช้
const NURSE_OUTCOME = {
  back_to_class: 'กลับเข้าเรียน',
  observe: 'สังเกตอาการ',
  rest_here: 'นอนพักที่ห้องพยาบาล',
  send_home: 'ส่งกลับบ้าน',
  refer_hospital: 'ส่งโรงพยาบาล',
};

export default function StudentHealth() {
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const authHeader = { headers: { authorization: `Bearer ${getToken()}` } };
  const yearId = localStorage.getItem('yearId');

  const [students, setStudents]     = useState([]);
  const [selectedSt, setSelectedSt] = useState('');  // st_id
  const [studentInfo, setStudentInfo] = useState(null);
  const [form, setForm]             = useState(EMPTY);
  const [loadingForm, setLoadingForm] = useState(false);
  const [saving, setSaving]         = useState(false);
  // ประวัติห้องพยาบาลล่าสุด — อ่านอย่างเดียว ครูประจำชั้นเห็นแค่วันที่/อาการ/ผลลัพธ์
  // (สัญญาณชีพ ยา และบันทึกการรักษา ไม่ถูกส่งมาที่ endpoint นี้ ดู nurseController)
  const [nurseVisits, setNurseVisits] = useState([]);

  // Load student list in advisor's room
  useEffect(() => {
    if (!yearId) return;
    const advisorId = getUserId();
    setLoading(true);
    axiosInstance.get(`${API}/getStudentsByAdvisorRoom/${advisorId}/${yearId}`, authHeader)
    .then(res => setStudents(res.data || []))
    .catch(() => setStudents([]))
    .finally(() => setLoading(false));
  }, [yearId]);

  // Load health data when student selected
  const loadHealth = useCallback((st_id) => {
    if (!st_id) return;
    setLoadingForm(true);
    axiosInstance.get(`${API}/getStudentHealth/${st_id}`, authHeader)
    .then(res => {
      const d = res.data;
      setStudentInfo(d);
      setForm(Object.fromEntries(FIELDS.map(f => [f.key, d[f.key] ?? ''])));
    })
    .catch(() => {
      setStudentInfo(null);
      setForm(EMPTY);
    })
    .finally(() => setLoadingForm(false));

    axiosInstance.get(`${API}/nurse/student-summary/${st_id}`, authHeader)
    .then(res => setNurseVisits(res.data || []))
    .catch(() => setNurseVisits([]));
  }, []);

  const handleSelectStudent = (e) => {
    const st_id = e.target.value;
    setSelectedSt(st_id);
    loadHealth(st_id);
  };

  const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = () => {
    if (!selectedSt) return;
    setSaving(true);
    axiosInstance.put(`${API}/updateStudentHealth`, { st_id: selectedSt, ...form }, authHeader)
    .then(() => {
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    })
    .catch(err => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถบันทึกได้' });
    })
    .finally(() => setSaving(false));
  };

  const roomLabel = students.length > 0
    ? `ม.${students[0].tb_class_c_id}/${students[0].r_no}`
    : '';

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item active">บันทึกสุขภาพนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">บันทึกสุขภาพนักเรียน
              {roomLabel && <span className="fs-sm text-muted ms-2">ห้อง {roomLabel}</span>}
            </h4>
          </div>
        </div>

        <Row className="g-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">เลือกนักเรียน</Form.Label>
                  <Form.Select
                    style={{ maxWidth: 400 }}
                    value={selectedSt}
                    onChange={handleSelectStudent}
                  >
                    <option value="">-- เลือกนักเรียน --</option>
                    {students.map(s => (
                      <option key={s.st_id} value={s.st_id}>
                        {s.st_idnumber} — {s.st_name}
                        {s.st_status === '0' ? ' (พ้นสภาพ)' : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {selectedSt && (
                  <>
                    {loadingForm ? (
                      <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : (
                      <>
                        {studentInfo && (
                          <div className="d-flex align-items-center gap-3 mb-3 p-3 rounded" style={{ background: '#f8f9fa' }}>
                            <div>
                              <span className="text-muted fs-sm">เลขประจำตัว</span>
                              <div className="fw-semibold">{studentInfo.st_idnumber}</div>
                            </div>
                            <div>
                              <span className="text-muted fs-sm">ชื่อ-สกุล</span>
                              <div className="fw-semibold">{studentInfo.st_name}</div>
                            </div>
                            <div>
                              <span className="text-muted fs-sm">สถานะ</span>
                              <div>
                                {studentInfo.st_status === '0'
                                  ? <Badge bg="danger">พ้นสภาพ</Badge>
                                  : <Badge bg="primary">กำลังศึกษา</Badge>}
                              </div>
                            </div>
                          </div>
                        )}

                        {nurseVisits.length > 0 && (
                          <div className="alert alert-light border py-2 mb-3">
                            <div className="fw-semibold fs-sm mb-1">
                              <i className="ri-first-aid-kit-line me-1"></i>ประวัติห้องพยาบาลล่าสุด
                            </div>
                            <div className="fs-xs">
                              {nurseVisits.map(v => (
                                <div key={v.nv_id}>
                                  {v.nv_date} · {v.ns_name || 'ไม่ระบุอาการ'} · {NURSE_OUTCOME[v.nv_outcome] || v.nv_outcome}
                                  {!v.nv_check_out && <span className="text-warning ms-1">(ยังอยู่ห้องพยาบาล)</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <Row className="g-3">
                          {FIELDS.map(f => (
                            <Col key={f.key} xl="6">
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">{f.label}</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  placeholder={`กรอก${f.label}`}
                                  value={form[f.key]}
                                  onChange={e => handleChange(f.key, e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                          ))}
                        </Row>

                        <div className="mt-4 d-flex justify-content-end">
                          <Button variant="success" onClick={handleSave} disabled={saving}>
                            {saving
                              ? <><Spinner animation="border" size="sm" className="me-1" />กำลังบันทึก...</>
                              : <><i className="ri-save-line me-1"></i>บันทึกข้อมูล</>}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
